import { SortCriteria } from '@app/taskpane/settings/sort-setting/sort-setting.component';
import { Sorting, WsFile } from '@model/ftf-api.model';

import { formatDateYYYYMMDD } from './file-utils';

export function compareStrings(a: string | null | undefined, b: string | null | undefined): number {
  if (a === b) return 0;
  if (a === null || a === undefined) return -1;
  if (b === null || b === undefined) return 1;
  return a.localeCompare(b);
}

/**
 * Compares two files by the specified criteria.
 * @param a The first file.
 * @param b The second file.
 * @param sortBy The criteria to sort by.
 * @param sortDirection The direction to sort (ascending or descending).
 * @returns A negative number if a < b, a positive number if a > b, and 0 if they are equal.
 */
export const compareFilesByCriteria = (a: WsFile, b: WsFile, sortBy: Sorting.SortByEnum, sortDirection: Sorting.DirectionEnum): number => {
  const direction = sortDirection === 'asc' ? 1 : -1;

  switch (sortBy) {
    case 'title': {
      return compareStrings(a.title, b.title) * direction;
    }

    case 'document_date':
      if (a.date && b.date) {
        return (a.date.getTime() - b.date.getTime()) * direction;
      } else if (a.date) {
        return -1 * direction;
      } else if (b.date) {
        return 1 * direction;
      }
      return 0;

    case 'document_sub_type': {
      return compareStrings(a.docSubtype, b.docSubtype) * direction;
    }

    case 'file_name': {
      return compareStrings(a.fileName, b.fileName) * direction;
    }

    default:
      return 0;
  }
};
/**
 * Creates a multi-criteria sort function based on the provided sort criteria.
 * @param sortCriteria The criteria to sort by.
 * @returns A function that compares two files based on the sort criteria.
 */
export const createMultiCriteriaSortFunction = (sortCriteria: SortCriteria[]) => {
  return (a: WsFile, b: WsFile): number => {
    // If no criteria provided, use a simple default
    if (!sortCriteria || sortCriteria.length === 0) {
      return defaultTrifoldSort(a, b);
    }

    // Go through each criteria in order
    for (const criteria of sortCriteria) {
      const result = compareFilesByCriteria(a, b, criteria.field as Sorting.SortByEnum, criteria.direction as Sorting.DirectionEnum);

      if (result !== 0) {
        return result;
      }
    }

    return 0;
  };
};

/**
 * Compares two files by their default trifold criteria.
 * @param a The first file.
 * @param b The second file.
 * @returns A negative number if a < b, a positive number if a > b, and 0 if they are equal.
 */
export const defaultTrifoldSort = (a: WsFile, b: WsFile): number => {
  if (a.date && b.date) {
    const dateResult = b.date.getTime() - a.date.getTime();
    if (dateResult !== 0) return dateResult;
  } else if (a.date) {
    return -1;
  } else if (b.date) {
    return 1;
  }

  /**
   * Compare by title, then file name, then document subtype.
   */
  const titleResult = compareStrings(a.title, b.title);
  if (titleResult !== 0) return titleResult;

  const fileNameResult = compareStrings(a.fileName, b.fileName);
  if (fileNameResult !== 0) return fileNameResult;

  return compareStrings(a.docSubtype, b.docSubtype);
};

/**
 * adjusts a trifold file's date properties to ensure consistency.
 * @param doc The trifold file to adjust.
 * @returns The adjusted trifold file.
 */
export const adjustTrifoldFile = (doc: WsFile): WsFile => {
  // Create a shallow copy of the document to avoid modifying read-only properties
  const adjustedDoc = { ...doc };

  if (adjustedDoc.documentDateTime) {
    // if we have the date time.. we need to adjust the date only version
    // to compensate for tz
    adjustedDoc.documentDate = formatDateYYYYMMDD(adjustedDoc.documentDateTime);
    adjustedDoc.date = new Date(adjustedDoc.documentDateTime);
  } else if (adjustedDoc.documentDate) {
    // convert to local tz date
    const parts = adjustedDoc.documentDate.split('-');
    adjustedDoc.date = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
  }
  return adjustedDoc;
};

/**
 * Adjusts the date properties of a list of trifold files.
 * @param docs The list of trifold files to adjust.
 * @returns The adjusted list of trifold files.
 */
export const adjustTrifoldFiles = (docs: WsFile[]) => {
  return docs.map(adjustTrifoldFile).sort(defaultTrifoldSort);
};

/**
 * Adjusts the date properties of a list of trifold files based on the provided sort criteria.
 * @param docs The list of trifold files to adjust.
 * @param sortCriteria The criteria to sort by.
 * @returns The adjusted list of trifold files.
 */
export const adjustTrifoldFilesWithCriteria = (docs: WsFile[], sortCriteria?: SortCriteria[]): WsFile[] => {
  const adjustedDocs = docs.map(adjustTrifoldFile);

  if (sortCriteria && sortCriteria.length > 0) {
    return adjustedDocs.sort(createMultiCriteriaSortFunction(sortCriteria));
  }

  // Otherwise use the default sort
  return adjustedDocs.sort(defaultTrifoldSort);
};
